"use client";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowRightIcon,
  BookOpenIcon,
  CalculatorIcon,
  CompassIcon,
  GraduationCapIcon,
  NewspaperIcon,
  PackageIcon,
} from "lucide-react";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { trackEvent } from "@/lib/analytics";
import { useIsClient } from "@/lib/hooks/useIsClient";
import { useLocalStorage } from "@/lib/hooks/useLocalStorage";
import { IndicatorDot } from "./ui/indicator-dot";
import { UnderlineLink } from "./primitives/underline-link";
import { ChangelogEntry } from "@/lib/content/changelog";
import { z } from "zod";
const STORAGE_KEY = "scoopulator:changelog-last-viewed" as const;
const lastViewedSchema = z.string().nullable();
const TAG_BADGE_VARIANTS = {
  feature: "pink",
  improvement: "indigo",
  UX: "violet",
  library: "amber",
} as const satisfies Partial<
  Record<string, NonNullable<BadgeProps["variant"]>>
>;
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
function getTagBadgeVariant(tag: string): BadgeProps["variant"] {
  switch (tag) {
    case "feature":
    case "improvement":
    case "UX":
    case "library":
      return TAG_BADGE_VARIANTS[tag];
    default:
      return "outline";
  }
}
function getTagBadgeIcon(tag: string) {
  switch (tag) {
    case "recipes":
      return <BookOpenIcon className="text-muted-foreground" size={12} />;
    case "calculator":
      return <CalculatorIcon className="text-muted-foreground" size={12} />;
    case "discover":
      return <CompassIcon className="text-muted-foreground" size={12} />;
    case "learn":
      return <GraduationCapIcon className="text-muted-foreground" size={12} />;
    case "ingredients":
      return <PackageIcon className="text-muted-foreground" size={12} />;
    default:
      return null;
  }
}
function ChangelogTag({ tag }: { tag: string }) {
  const variant = getTagBadgeVariant(tag);
  const icon = getTagBadgeIcon(tag);
  return (
    <Badge size="sm" variant={variant} className="flex gap-1 capitalize">
      {icon}
      {tag}
    </Badge>
  );
}
export function ChangelogMenu({ entries }: { entries: ChangelogEntry[] }) {
  const isClient = useIsClient();
  const [open, setOpen] = useState(false);
  const [lastViewed, setLastViewed] = useLocalStorage<string | null>(
    STORAGE_KEY,
    null,
    { schema: lastViewedSchema },
  );
  const latest = entries[0];
  const hasUnseen =
    isClient &&
    latest != null &&
    (lastViewed == null || lastViewed !== latest.slug);
  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (open && latest) {
      setLastViewed(latest.slug);
      trackEvent("changelog-menu-opened", { hasUnseen: String(hasUnseen) });
    }
  };
  if (entries.length === 0) {
    return null;
  }
  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        render={
          <Button variant="ghost" size="icon" aria-label="Recent changes" />
        }
      >
        <NewspaperIcon className="text-muted-foreground" />
        {hasUnseen ? (
          <>
            <IndicatorDot
              className="absolute top-2 right-1.5"
              border="--card"
            />
            <span className="sr-only">New updates</span>
          </>
        ) : null}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-88 max-w-full overflow-hidden p-0"
      >
        <div className="grid grid-cols-[1fr_auto] border-b px-4 py-3">
          <p className="font-semibold">What&apos;s new</p>
          <UnderlineLink
            className="flex items-center gap-1 text-sm"
            to="/changelog"
            onClick={() => {
              setOpen(false);
              trackEvent("changelog-menu-view-all-clicked");
            }}
          >
            See more <ArrowRightIcon className="h-4 w-4" />
          </UnderlineLink>
        </div>
        <ul className="pb-2">
          {entries.map((entry) => (
            <li key={entry.slug}>
              <Link
                to={entry.url}
                onClick={() => {
                  setOpen(false);
                  trackEvent("changelog-menu-entry-clicked", {
                    slug: entry.slug,
                  });
                }}
                aria-label={`View changelog entry: ${entry.title}`}
              >
                <div className="hover:bg-accent px-4 py-3">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      {entry.tags?.length ? (
                        <div className="mb-2 flex flex-wrap gap-1">
                          {entry.tags.map((tag) => (
                            <ChangelogTag key={tag} tag={tag} />
                          ))}
                        </div>
                      ) : null}
                      <p className="text-muted-foreground text-xs">
                        {formatDate(entry.publishedAt)}
                      </p>
                    </div>
                    {entry.image ? (
                      <div className="bg-muted relative shrink-0 overflow-hidden rounded-sm">
                        <img
                          src={entry.image}
                          alt={entry.title}
                          height={40}
                          width={70}
                          className="object-cover"
                        />
                      </div>
                    ) : null}
                  </div>
                  <p className="mt-0.5 text-sm font-medium">{entry.title}</p>
                  {entry.summary ? (
                    <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
                      {entry.summary}
                    </p>
                  ) : null}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
